import { useEffect, useRef, useState } from "react";
import { distanceMeters } from "../shared/time";
import { hasLeftOrigin, homeAt } from "../shared/origins";
import type { Coordinates, RiderPhase } from "../shared/types";
import {
	advanceStationCursor,
	EMPTY_STATION_CURSOR,
	isRepeatFix,
	shouldRecordStationFix,
} from "../core/station-telemetry";
import { MAP_STATIONS } from "./bart-network";
import { report } from "./report";
import type { NativeFix } from "../shared/native-bridge";

/**
 * The part of a reading anything here actually looks at.
 *
 * `GeolocationPosition` satisfies it structurally, so the browser watch is unchanged, and a fix
 * handed over by the native shell can be given to the same function rather than to a copy of it.
 */
interface Reading {
	coords: {
		latitude: number;
		longitude: number;
		accuracy: number;
		speed: number | null;
		altitude: number | null;
		altitudeAccuracy: number | null;
		heading: number | null;
	};
	timestamp: number;
}

/** iOS supplies altitude and heading too; until the bridge carries them they are simply absent. */
function asReading(fix: NativeFix): Reading {
	return {
		coords: {
			latitude: fix.lat,
			longitude: fix.lon,
			accuracy: fix.accuracyMeters,
			speed: fix.speedMetersPerSecond,
			altitude: null,
			altitudeAccuracy: null,
			heading: null,
		},
		timestamp: fix.atMs,
	};
}

/** Where fixes come from. `active` is the shell being present, which is fixed for the session. */
export interface NativeLocation {
	active: boolean;
	fix: NativeFix | null;
}

/** The leg being recorded, or null while nothing is. */
export interface StationRecord {
	phase: RiderPhase;
	leg: "school" | "home";
}

export interface GeolocationState {
	position: (Coordinates & { accuracyMeters: number; speedMetersPerSecond: number | null }) | null;
	error: string | null;
	pending: boolean;
	/**
	 * Live: away from the saved starting point and away from either house.
	 *
	 * Standing in one of the two houses is proof the drive has not started, whichever of them
	 * the commute happens to begin from. Without that second half, choosing the commute that
	 * starts at the other house read as a drive already under way while the phone sat still.
	 */
	awayFromHome: boolean;
	/** Null when there is no saved address to measure from. */
	metersFromHome: number | null;
	/** Which house the phone is standing at, which is what forbids drive mode. */
	atHomeLabel: string | null;
	/** When the fix was taken, so a position that has stopped updating is visible as one. */
	fixAtMs: number | null;
	/**
	 * Where the phone was last seen, kept through a watch teardown.
	 *
	 * `position` is deliberately cleared when the watch stops, because a stale fix must not be
	 * mistaken for a live one by anything deciding where he is. But "how far is the house" does
	 * not need a live one, and answering it with a dash while the phone is underground at Civic
	 * Center is worse than answering it from two minutes ago.
	 */
	lastPosition: Coordinates | null;
}

/** Ignore jitter below this so a stationary phone does not trigger repeated recalculation. */
const SIGNIFICANT_MOVE_METERS = 25;
const TRAVEL_DISTANCE_METERS = 40;
const TRAVEL_SPEED_METERS_PER_SECOND = 1.2;
const TRAVEL_CONFIRMATIONS = 2;

/** Below this the phone was put away rather than glanced at, and the gap is worth a row. */
const WORTH_REPORTING_AWAY_MS = 60_000;

/** The nearest platform to a point, however far off it is: on this line that is the whole map. */
function nearestStation(point: Coordinates): { id: string; meters: number } {
	const best = Object.values(MAP_STATIONS)
		.map((entry) => ({ id: entry.id, meters: distanceMeters(point, entry) }))
		.sort((left, right) => left.meters - right.meters);
	return best[0]!;
}

/**
 * `home` is the saved starting point, which anchors detection far better than the first fix.
 *
 * `record` turns on the journey recorder: every fix is kept while it is set, which is how the
 * approach to a platform gets measured rather than guessed at.
 */
export function useGeolocation(
	enabled: boolean,
	home: Coordinates | null,
	simulatedAt: Coordinates | null = null,
	record: StationRecord | null = null,
	native: NativeLocation = { active: false, fix: null },
	reportMapFixes = false,
): GeolocationState {
	const mapReportingRef = useRef(reportMapFixes);
	mapReportingRef.current = reportMapFixes;
	const lastMapReportRef = useRef(0);
	const [state, setState] = useState<GeolocationState>({
		position: null,
		error: null,
		pending: enabled,
		awayFromHome: false,
		metersFromHome: null,
		atHomeLabel: null,
		fixAtMs: null,
		lastPosition: null,
	});
	const lastRef = useRef<Coordinates | null>(null);
	const originRef = useRef<(Coordinates & { accuracyMeters: number }) | null>(null);
	const confirmationsRef = useRef(0);
	// Every fix, not just the ones worth a row: this is the anchor a gap is measured from.
	const seenRef = useRef<(Coordinates & { atMs: number }) | null>(null);
	const hiddenAtRef = useRef<number | null>(null);
	const resumeRef = useRef<{ from: Coordinates & { atMs: number }; awayMs: number } | null>(null);
	const homeRef = useRef<Coordinates | null>(home);
	homeRef.current = home;
	// Held in a ref, not a dependency: restarting the watch to change what it records would throw
	// away the fix continuity the recording is for.
	const recordRef = useRef(record);
	recordRef.current = record;
	const stationCursorRef = useRef(EMPTY_STATION_CURSOR);
	// Held so a fix arriving from the shell can be given to the live handler without restarting the
	// watch, which would throw away the fix counters and the significant-move anchor.
	const onFixRef = useRef<((reading: Reading) => void) | null>(null);
	const nativeActive = native.active;
	const nativeFix = native.fix;

	// One budget per leg, so the walk out of school and the morning wait do not share it, and
	// stepping through phases within a leg does not restart it.
	const recordLeg = record?.leg ?? null;
	useEffect(() => {
		stationCursorRef.current = EMPTY_STATION_CURSOR;
	}, [recordLeg]);

	useEffect(() => {
		// Standing somewhere by hand takes the wheel entirely; a real fix would only argue with it.
		if (simulatedAt) {
			return;
		}
		if (!enabled) {
			// Tearing the watch down must not un-leave him, and must not forget where he was.
			setState((current) => ({ ...current, position: null, error: null, pending: false }));
			return;
		}
		if (!("geolocation" in navigator)) {
			setState((current) => ({ ...current, error: "This browser has no location support.", pending: false }));
			report("location-unsupported", {});
			return;
		}

		report("location-watch", { started: true, source: nativeActive ? "native" : "browser" });
		/*
		 * The last fix belongs to the last watch.
		 *
		 * Tearing the watch down clears `position` but this ref survives it, so a phone that has
		 * not moved since would fail the significant-move test below on every fix and never get a
		 * position back at all. The morning hides it — the watch starts and he then drives off —
		 * but Pickup Mode starts the watch while he is sitting still, and sat on "working out the
		 * drive" for as long as he stayed there.
		 */
		lastRef.current = null;
		let fixes = 0;
		let reportedTraveling = false;
		const onFix = (fix: Reading) => {
				const next = { lat: fix.coords.latitude, lon: fix.coords.longitude };
				const accuracyMeters = fix.coords.accuracy;
				const speedMetersPerSecond = fix.coords.speed;
				if (mapReportingRef.current && document.visibilityState === "visible" &&
					fix.timestamp > lastMapReportRef.current && Date.now() - fix.timestamp <= 30_000 &&
					fix.timestamp <= Date.now() && Number.isFinite(accuracyMeters) && accuracyMeters >= 0 &&
					fix.timestamp - lastMapReportRef.current >= 15_000) {
					lastMapReportRef.current = fix.timestamp;
					report("map-fix", { ...next, accuracyMeters, speedMetersPerSecond, fixAtMs: fix.timestamp });
				}
				// The only trace a suspended app can leave: where he was, where he reappeared, and how
				// long it took. A whole ride happens inside one of these, so the implied speed is the
				// evidence that he was on a train rather than walking or waiting.
				const resume = resumeRef.current;
				resumeRef.current = null;
				if (resume && resume.awayMs >= WORTH_REPORTING_AWAY_MS) {
					const meters = distanceMeters(resume.from, next);
					const seconds = Math.max(1, Math.round((fix.timestamp - resume.from.atMs) / 1000));
					const from = nearestStation(resume.from);
					const to = nearestStation(next);
					report("location-resume", {
						awayMs: resume.awayMs,
						gapSeconds: seconds,
						metersMoved: Math.round(meters),
						impliedSpeed: Math.round((meters / seconds) * 10) / 10,
						fromStation: from.id,
						fromStationMeters: Math.round(from.meters),
						toStation: to.id,
						toStationMeters: Math.round(to.meters),
						accuracyMeters: Math.round(accuracyMeters),
						online: navigator.onLine,
					});
				}
				seenRef.current = { ...next, atMs: fix.timestamp };
				originRef.current ??= { ...next, accuracyMeters };
				const travelThreshold = Math.max(
					TRAVEL_DISTANCE_METERS,
					Math.min(100, originRef.current.accuracyMeters + accuracyMeters),
				);
				// Distance from the house is the one measure a restart cannot corrupt, so it
				// decides on its own; the first-fix comparison only survives as a fallback for
				// when there is no saved address.
				const metersFromHome = homeRef.current == null ? null : distanceMeters(homeRef.current, next);
				const standingAt = homeAt(next);
				const leftHome = hasLeftOrigin(homeRef.current, next, accuracyMeters);
				const displaced = distanceMeters(originRef.current, next) >= travelThreshold;
				const moving = speedMetersPerSecond != null && speedMetersPerSecond >= TRAVEL_SPEED_METERS_PER_SECOND;
				confirmationsRef.current = standingAt == null && (displaced || moving) ? confirmationsRef.current + 1 : 0;
				// Kept as evidence rather than as an answer: the journey's phase decides whether he has
				// gone, and this is one of the things it reads.
				const movingByFix = leftHome || confirmationsRef.current >= TRAVEL_CONFIRMATIONS;
				fixes += 1;
				// The first fix, then only the ones that change the answer: enough to reconstruct a
				// drive afterwards without writing a row every second of it.
				if (fixes === 1 || movingByFix !== reportedTraveling) {
					reportedTraveling = movingByFix;
					report("location-fix", {
						fixes,
						accuracyMeters: Math.round(accuracyMeters),
						speedMetersPerSecond,
						metersFromFirstFix: Math.round(distanceMeters(originRef.current, next)),
						metersFromHome: metersFromHome == null ? null : Math.round(metersFromHome),
						threshold: Math.round(travelThreshold),
						leftHome,
						standingAt: standingAt?.id ?? null,
						displaced,
						movingBySpeed: moving,
						confirmations: confirmationsRef.current,
						moving: movingByFix,
					});
				}
				const recording = recordRef.current;
				if (recording) {
					const nowMs = Date.now();
					const sample = { fixAtMs: fix.timestamp };
					if (shouldRecordStationFix(stationCursorRef.current, sample, nowMs)) {
						const repeat = isRepeatFix(stationCursorRef.current, sample);
						const station = nearestStation(next);
						stationCursorRef.current = advanceStationCursor(stationCursorRef.current, sample, nowMs);
						report("station-fix", {
							leg: recording.leg,
							phase: recording.phase,
							repeat,
							fixAtMs: fix.timestamp,
							// How old the fix already was when it arrived: a live watch reads near zero.
							fixAgeMs: Math.max(0, nowMs - fix.timestamp),
							accuracyMeters: accuracyMeters,
							// Never collected before, and the most promising evidence of a descent.
							altitudeMeters: fix.coords.altitude,
							altitudeAccuracyMeters: fix.coords.altitudeAccuracy,
							speedMetersPerSecond,
							headingDegrees: fix.coords.heading,
							lat: Math.round(next.lat * 1e6) / 1e6,
							lon: Math.round(next.lon * 1e6) / 1e6,
							nearestStation: station.id,
							nearestStationMeters: Math.round(station.meters),
							rows: stationCursorRef.current.rows,
							visibility: document.visibilityState,
							online: navigator.onLine,
						});
					}
				}
				const previous = lastRef.current;
				if (previous && distanceMeters(previous, next) < SIGNIFICANT_MOVE_METERS) {
					setState((current) => ({
						...current,
						pending: false,
						awayFromHome: leftHome,
						metersFromHome,
						atHomeLabel: standingAt?.shortLabel ?? null,
						fixAtMs: fix.timestamp,
						lastPosition: next,
					}));
					return;
				}
				lastRef.current = next;
				setState(() => ({
					position: { ...next, accuracyMeters, speedMetersPerSecond },
					error: null,
					pending: false,
					awayFromHome: leftHome,
					metersFromHome,
					atHomeLabel: standingAt?.shortLabel ?? null,
					fixAtMs: fix.timestamp,
					lastPosition: next,
				}));
		};

		const onFailure = (failure: GeolocationPositionError) => {
			report("location-error", { code: failure.code, message: failure.message });
			setState((current) => ({
				...current,
				error: failure.code === failure.PERMISSION_DENIED ? "Location permission denied." : "Could not get a location fix.",
				pending: false,
			}));
		};

		const options: PositionOptions = { enableHighAccuracy: true, maximumAge: 0, timeout: 15_000 };

		/*
		 * In the shell, CoreLocation is the only source. A browser watch alongside it would stop at
		 * the lock screen and argue with the fixes that kept coming, and the shell's are better —
		 * they carry a speed accuracy and survive a pocketed phone.
		 */
		if (nativeActive) {
			onFixRef.current = onFix;
			return () => {
				onFixRef.current = null;
			};
		}

		const watchId = navigator.geolocation.watchPosition(onFix, onFailure, options);

		// iOS stops the watch while the app is in the background, and on return it can answer
		// with the fix it had when the drive began. A whole drive reported twenty-five metres
		// from the house that way. Asking outright on resume is the only cure.
		const onVisible = () => {
			if (document.visibilityState !== "visible") {
				hiddenAtRef.current = Date.now();
				return;
			}
			const hiddenAt = hiddenAtRef.current;
			hiddenAtRef.current = null;
			if (hiddenAt != null && seenRef.current) {
				resumeRef.current = { from: seenRef.current, awayMs: Date.now() - hiddenAt };
			}
			navigator.geolocation.getCurrentPosition(onFix, onFailure, options);
		};
		document.addEventListener("visibilitychange", onVisible);

		return () => {
			document.removeEventListener("visibilitychange", onVisible);
			navigator.geolocation.clearWatch(watchId);
		};
	}, [enabled, simulatedAt, nativeActive]);

	// A fix from the shell goes to the handler the watch would have used, so leaving home, station
	// telemetry and the resume record all behave identically whoever produced the reading.
	useEffect(() => {
		// Standing somewhere by hand takes the wheel here too. The browser watch above already
		// stands aside; without the same guard CoreLocation carried on reporting the real place, so
		// a phone simulated to College Prep observed itself at home and reset put it back there.
		if (!enabled || simulatedAt || !nativeFix) {
			return;
		}
		onFixRef.current?.(asReading(nativeFix));
	}, [nativeFix, simulatedAt, enabled, nativeActive]);

	/**
	 * Standing somewhere by hand, which is the only way to try a morning that starts at the other
	 * house without driving to it. Reads exactly as a perfect fix at that spot, so everything
	 * downstream — leaving home, reaching the station — behaves as it would there.
	 */
	useEffect(() => {
		if (!simulatedAt) {
			return;
		}
		const house = homeAt(simulatedAt);
		const metersFromHome = homeRef.current == null ? null : distanceMeters(homeRef.current, simulatedAt);
		// A simulated pin is exact, so it is offered as a perfect fix rather than a vague one.
		const leftHome = hasLeftOrigin(homeRef.current, simulatedAt, 0);
		const update = () => {
			const fixAtMs = Date.now();
			report("manual-fix", { ...simulatedAt, accuracyMeters: 5, speedMetersPerSecond: null, fixAtMs, source: "manual-test" });
			setState(() => ({
				position: { ...simulatedAt, accuracyMeters: 5, speedMetersPerSecond: null },
				error: null,
				pending: false,
				awayFromHome: leftHome,
				metersFromHome,
				atHomeLabel: house?.shortLabel ?? null,
				fixAtMs,
				lastPosition: simulatedAt,
			}));
		};
		update();
		const timer = window.setInterval(update, 15_000);
		return () => window.clearInterval(timer);
	}, [simulatedAt, home]);

	return state;
}
